import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import '../styles/fellowship-animation.css';

const INK       = '#00224D';
const GOLD      = '#f59e0b';
const SP        = { type: 'spring', stiffness: 260, damping: 20 };
const SP_FAST   = { type: 'spring', stiffness: 320, damping: 18 };
const SP_BOUNCE = { type: 'spring', stiffness: 400, damping: 15 };
const EASE      = [0.4, 0, 0.2, 1];

/* ── useYOMCounter ─────────────────────────────────────────────── */
function useYOMCounter(active, target = 500, delay = 0) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) { setCount(0); return; }
    let raf;
    const timer = setTimeout(() => {
      const start = performance.now();
      const duration = 1200;
      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        setCount(Math.round(t * target));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay * 1000);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [active, target, delay]);
  return count;
}

/* ── DoodlePerson ─────────────────────────────────────────────── */
function DoodlePerson({ cx, cy, show, delay = 0, size = 1, arms = 'down', thick = false, glowColor = null }) {
  const s   = size;
  const sw  = thick ? 2.5 : 1.8;
  const hy  = cy - 40 * s;
  const hr  = 16 * s;

  const body  = `M${cx},${hy+hr} C${cx-s},${cy-5*s} ${cx+s},${cy+8*s} ${cx},${cy+26*s}`;
  const legL  = `M${cx},${cy+26*s} C${cx-4*s},${cy+40*s} ${cx-9*s},${cy+52*s} ${cx-11*s},${cy+62*s}`;
  const legR  = `M${cx},${cy+26*s} C${cx+4*s},${cy+40*s} ${cx+9*s},${cy+52*s} ${cx+11*s},${cy+62*s}`;
  const smile = `M${cx-7*s},${hy+5*s} Q${cx},${hy+12*s} ${cx+7*s},${hy+5*s}`;

  const shoulderY = hy + hr + 8 * s;
  let armL, armR;
  if (arms === 'out') {
    armL = `M${cx-2*s},${shoulderY} L${cx-24*s},${shoulderY}`;
    armR = `M${cx+2*s},${shoulderY} L${cx+24*s},${shoulderY}`;
  } else if (arms === 'up') {
    armL = `M${cx-2*s},${cy-12*s} C${cx-16*s},${cy-22*s} ${cx-22*s},${cy-34*s} ${cx-14*s},${cy-46*s}`;
    armR = `M${cx+2*s},${cy-12*s} C${cx+16*s},${cy-22*s} ${cx+22*s},${cy-34*s} ${cx+14*s},${cy-46*s}`;
  } else {
    armL = `M${cx-2*s},${cy-12*s} C${cx-10*s},${cy-4*s} ${cx-20*s},${cy+2*s} ${cx-24*s},${cy+12*s}`;
    armR = `M${cx+2*s},${cy-12*s} C${cx+10*s},${cy-4*s} ${cx+20*s},${cy+2*s} ${cx+24*s},${cy+12*s}`;
  }

  const D = (extra) => ({
    initial:    { pathLength: 0, opacity: 0 },
    animate:    { pathLength: show ? 1 : 0, opacity: show ? 1 : 0 },
    transition: { duration: 0.42, ease: EASE, delay: delay + extra },
  });

  return (
    <g>
      {glowColor && (
        <motion.circle cx={cx} cy={hy} r={32 * s} fill={glowColor}
          initial={{ scale: 0, opacity: 0 }}
          animate={show
            ? { scale: [1, 1.15, 1], opacity: [0.12, 0.20, 0.12] }
            : { scale: 0, opacity: 0 }}
          transition={show
            ? { duration: 2, repeat: Infinity, delay: delay + 0.6 }
            : { ...SP, delay }}
        />
      )}
      <motion.circle cx={cx} cy={hy} r={hr}
        stroke={INK} strokeWidth={sw} fill="rgba(255,255,255,0.60)"
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: show ? 1 : 0, opacity: show ? 1 : 0 }}
        transition={{ ...SP, delay }}
      />
      {[-5 * s, 5 * s].map((dx, i) => (
        <motion.circle key={i} cx={cx + dx} cy={hy - s} r={1.5 * s} fill={INK}
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          initial={{ scale: 0 }} animate={{ scale: show ? 1 : 0 }}
          transition={{ ...SP, delay: delay + 0.10 }}
        />
      ))}
      <motion.path d={smile} stroke={INK} strokeWidth={sw * 0.8} fill="none" strokeLinecap="round" {...D(0.14)} />
      <motion.path d={body}  stroke={INK} strokeWidth={sw}       fill="none" strokeLinecap="round" {...D(0.06)} />
      <motion.path d={armL}  stroke={INK} strokeWidth={sw}       fill="none" strokeLinecap="round" {...D(0.18)} />
      <motion.path d={armR}  stroke={INK} strokeWidth={sw}       fill="none" strokeLinecap="round" {...D(0.22)} />
      <motion.path d={legL}  stroke={INK} strokeWidth={sw}       fill="none" strokeLinecap="round" {...D(0.28)} />
      <motion.path d={legR}  stroke={INK} strokeWidth={sw}       fill="none" strokeLinecap="round" {...D(0.32)} />
    </g>
  );
}

/* ── DoodleCoin ───────────────────────────────────────────────── */
function DoodleCoin({ cx, cy, toY, show, delay = 0, label = '+30' }) {
  return (
    <motion.g
      initial={{ y: 0, opacity: 0 }}
      animate={show ? { y: toY - cy, opacity: [0, 1, 1, 0] } : { y: 0, opacity: 0 }}
      transition={{ duration: 1.0, ease: EASE, delay }}
    >
      <circle cx={cx} cy={cy} r={16} stroke={GOLD} strokeWidth={2.5} fill="#fef3c7" />
      <circle cx={cx} cy={cy} r={10} stroke={GOLD} strokeWidth={1}   fill="none" />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize={7} fontWeight={700}
        fill="#b45309" fontFamily="Inter, system-ui, sans-serif">{label}</text>
    </motion.g>
  );
}

/* ── DoodleSparkle ────────────────────────────────────────────── */
function DoodleSparkle({ cx, cy, show, delay = 0, color = GOLD, size = 1 }) {
  const L = 9 * size;
  return (
    <g>
      {[0, 45, 90, 135].map((a, i) => {
        const rad = a * Math.PI / 180;
        const cos = Math.cos(rad), sin = Math.sin(rad);
        return [
          <motion.line key={`a${i}`}
            x1={cx + L * 0.2 * cos} y1={cy + L * 0.2 * sin}
            x2={cx + L * cos}       y2={cy + L * sin}
            stroke={color} strokeWidth={1.8} strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: show ? 1 : 0, opacity: show ? 1 : 0 }}
            transition={{ duration: 0.3, delay: delay + i * 0.05 }}
          />,
          <motion.line key={`b${i}`}
            x1={cx - L * 0.2 * cos} y1={cy - L * 0.2 * sin}
            x2={cx - L * cos}       y2={cy - L * sin}
            stroke={color} strokeWidth={1.8} strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: show ? 1 : 0, opacity: show ? 1 : 0 }}
            transition={{ duration: 0.3, delay: delay + i * 0.05 + 0.02 }}
          />,
        ];
      })}
    </g>
  );
}

/* ── DoodleGift ───────────────────────────────────────────────── */
function DoodleGift({ x, y, show, delay = 0 }) {
  return (
    <motion.g
      style={{ transformBox: 'fill-box', transformOrigin: `${x + 18}px ${y + 28}px` }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: show ? 1 : 0, opacity: show ? 1 : 0 }}
      transition={{ ...SP, delay }}
    >
      <rect x={x}     y={y}     width={36} height={28} rx={3} stroke={INK} fill="#fef3c7" strokeWidth={2} />
      <rect x={x - 3} y={y - 8} width={42} height={10} rx={2} stroke={INK} fill="#fde68a" strokeWidth={2} />
      <line x1={x + 18} y1={y - 8}  x2={x + 18} y2={y + 28} stroke={GOLD} strokeWidth={1.5} strokeLinecap="round" />
      <line x1={x}      y1={y - 3}  x2={x + 36} y2={y - 3}  stroke={GOLD} strokeWidth={1.5} strokeLinecap="round" />
      <path d={`M${x+18},${y-8} C${x+8},${y-18} ${x+4},${y-24} ${x+18},${y-14}`}
        stroke={GOLD} strokeWidth={1.8} fill="none" strokeLinecap="round" />
      <path d={`M${x+18},${y-8} C${x+28},${y-18} ${x+32},${y-24} ${x+18},${y-14}`}
        stroke={GOLD} strokeWidth={1.8} fill="none" strokeLinecap="round" />
    </motion.g>
  );
}

/* ── DoodleBadge ──────────────────────────────────────────────── */
function DoodleBadge({ x, y, label, tier = 'associate', show, delay = 0 }) {
  const cfg = {
    associate:     { stroke: '#94a3b8', fill: '#f8fafc', textFill: '#1e293b' },
    fellow:        { stroke: GOLD,      fill: '#00224D', textFill: '#FFFFFF' },
    senior:        { stroke: GOLD,      fill: '#78350f', textFill: GOLD      },
    distinguished: { stroke: '#e2e8f0', fill: '#0f172a', textFill: '#e2e8f0' },
  }[tier] || { stroke: '#94a3b8', fill: '#f8fafc', textFill: '#1e293b' };

  return (
    <motion.g
      style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: show ? 1 : 0, opacity: show ? 1 : 0 }}
      transition={{ ...SP_FAST, delay }}
    >
      {tier === 'distinguished' && (
        <path d={`M${x+10},${y-8} L${x+20},${y-16} L${x+30},${y-8} L${x+40},${y-16} L${x+50},${y-8}`}
          stroke={GOLD} strokeWidth={2} fill="#fef3c7" strokeLinecap="round" strokeLinejoin="round" />
      )}
      <rect x={x} y={y} width={120} height={36} rx={6}
        fill={cfg.fill} stroke={cfg.stroke} strokeWidth={1.5} />
      <text x={x + 60} y={y + 23} textAnchor="middle" fontSize={10} fontWeight={800}
        fill={cfg.textFill} fontFamily="Inter, system-ui, sans-serif" letterSpacing={0.8}>{label}</text>
    </motion.g>
  );
}

/* ── DoodleTeacherPro ─────────────────────────────────────────── */
function DoodleTeacherPro({ cx, cy, show, delay = 0 }) {
  const hcy = cy - 57;        // head centre y (118 when cy=175)
  const NAVY = '#1a2e4a';
  const HAIR = '#3b2a1a';
  const SKIN = '#fdf6ee';     // light cream face fill

  const FD = (d, dur = 0.38) => ({
    initial: { opacity: 0 },
    animate: { opacity: show ? 1 : 0 },
    transition: { duration: dur, delay: delay + d },
  });
  const SC = (d) => ({
    style: { transformBox: 'fill-box', transformOrigin: 'center' },
    initial: { scale: 0, opacity: 0 },
    animate: { scale: show ? 1 : 0, opacity: show ? 1 : 0 },
    transition: { ...SP, delay: delay + d },
  });
  const PL = (d, dur = 0.44) => ({
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: show ? 1 : 0, opacity: show ? 1 : 0 },
    transition: { duration: dur, ease: EASE, delay: delay + d },
  });

  // Key y landmarks
  const neckTop   = hcy + 18;
  const neckBot   = hcy + 30;
  const shoulY    = hcy + 38;   // shoulder line
  const blazerBot = hcy + 100;
  const skirtBot  = hcy + 130;
  const legBot    = hcy + 168;

  return (
    <g>
      {/* ── Amber glow (behind everything) ── */}
      <motion.circle cx={cx} cy={hcy + 22} r={58}
        fill="rgba(255,200,80,0.22)"
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: show ? 1 : 0, opacity: show ? 1 : 0 }}
        transition={{ ...SP, delay }} />

      {/* ══ BODY ══════════════════════════════════════════════════════ */}

      {/* Dark navy blazer body */}
      <motion.path
        d={`M ${cx-16} ${neckBot}
            Q ${cx-27} ${neckBot+8} ${cx-28} ${shoulY}
            L ${cx-26} ${blazerBot}
            Q ${cx-26} ${blazerBot+8} ${cx-14} ${blazerBot+8}
            L ${cx+14} ${blazerBot+8}
            Q ${cx+26} ${blazerBot+8} ${cx+26} ${blazerBot}
            L ${cx+28} ${shoulY}
            Q ${cx+27} ${neckBot+8} ${cx+16} ${neckBot} Z`}
        fill={NAVY} stroke={NAVY} strokeWidth={1.5}
        strokeLinejoin="round" {...FD(0.05)} />

      {/* Skirt / lower half */}
      <motion.path
        d={`M ${cx-26} ${blazerBot}
            L ${cx-24} ${skirtBot}
            L ${cx+24} ${skirtBot}
            L ${cx+26} ${blazerBot}`}
        fill={NAVY} stroke={NAVY} strokeWidth={1.5}
        strokeLinejoin="round" {...FD(0.06)} />

      {/* White shirt strip visible between lapels */}
      <motion.path
        d={`M ${cx-6} ${neckBot}
            L ${cx} ${neckBot+15} L ${cx+6} ${neckBot}
            L ${cx+4} ${blazerBot-14} L ${cx-4} ${blazerBot-14} Z`}
        fill="white" {...FD(0.09)} />

      {/* Blazer button (white on navy) */}
      <motion.circle cx={cx} cy={hcy+70} r={2.5} fill="white" {...FD(0.22)} />

      {/* ══ NECK ══════════════════════════════════════════════════════ */}
      <motion.path
        d={`M ${cx-6} ${neckTop} L ${cx+6} ${neckTop}
            L ${cx+5} ${neckBot} L ${cx-5} ${neckBot} Z`}
        fill={SKIN} stroke={NAVY} strokeWidth={1.5} {...FD(0.04)} />

      {/* ══ SIDE HAIR (drawn BEFORE oval → peeks from behind face) ══ */}
      <motion.path
        d={`M ${cx-14} ${hcy-10} C ${cx-20} ${hcy} ${cx-20} ${hcy+10} ${cx-16} ${hcy+18}`}
        stroke={HAIR} strokeWidth={2.8} fill="none" strokeLinecap="round"
        {...PL(0.03, 0.22)} />
      <motion.path
        d={`M ${cx+14} ${hcy-10} C ${cx+20} ${hcy} ${cx+20} ${hcy+10} ${cx+16} ${hcy+18}`}
        stroke={HAIR} strokeWidth={2.8} fill="none" strokeLinecap="round"
        {...PL(0.03, 0.22)} />

      {/* ══ HEAD OVAL — light cream, navy outline ══════════════════════ */}
      <motion.ellipse cx={cx} cy={hcy} rx={17} ry={19}
        fill={SKIN} stroke={NAVY} strokeWidth={2}
        {...SC(0)} />

      {/* Ears */}
      <motion.path d={`M ${cx-17} ${hcy-4} Q ${cx-21} ${hcy+1} ${cx-17} ${hcy+8}`}
        stroke={NAVY} strokeWidth={1.5} fill="#fde8cc"
        strokeLinecap="round" {...PL(0.07, 0.18)} />
      <motion.path d={`M ${cx+17} ${hcy-4} Q ${cx+21} ${hcy+1} ${cx+17} ${hcy+8}`}
        stroke={NAVY} strokeWidth={1.5} fill="#fde8cc"
        strokeLinecap="round" {...PL(0.07, 0.18)} />

      {/* ══ HAIR BUN — centered on top of head, drawn AFTER oval ══════ */}
      <motion.circle cx={cx} cy={hcy - 17} r={10}
        fill={HAIR} stroke={NAVY} strokeWidth={1.5}
        {...SC(0.02)} />
      {/* Bun crease accent */}
      <motion.path d={`M ${cx-5} ${hcy-24} Q ${cx} ${hcy-27} ${cx+5} ${hcy-24}`}
        stroke="rgba(255,255,255,0.20)" strokeWidth={1} fill="none"
        strokeLinecap="round" {...PL(0.07, 0.16)} />

      {/* ══ FACE FEATURES ════════════════════════════════════════════ */}
      {/* Eyes: two simple navy dots, symmetrical, 4px */}
      <motion.circle cx={cx-6} cy={hcy-1} r={2.5} fill={NAVY} {...SC(0.12)} />
      <motion.circle cx={cx+6} cy={hcy-1} r={2.5} fill={NAVY} {...SC(0.13)} />
      {/* Smile: simple curve */}
      <motion.path d={`M ${cx-6} ${hcy+8} Q ${cx} ${hcy+13} ${cx+6} ${hcy+8}`}
        stroke={NAVY} strokeWidth={1.5} fill="none"
        strokeLinecap="round" {...PL(0.15, 0.26)} />

      {/* ══ ARMS ════════════════════════════════════════════════════ */}
      {/* Left arm — angles down toward clipboard */}
      <motion.path
        d={`M ${cx-26} ${shoulY} C ${cx-30} ${shoulY+14} ${cx-34} ${shoulY+26} ${cx-32} ${shoulY+40}`}
        stroke={NAVY} strokeWidth={2} fill="none" strokeLinecap="round"
        {...PL(0.20, 0.40)} />

      {/* Right arm — slightly raised, natural gesture */}
      <motion.path
        d={`M ${cx+26} ${shoulY} C ${cx+32} ${shoulY-6} ${cx+35} ${shoulY-14} ${cx+32} ${shoulY-22}`}
        stroke={NAVY} strokeWidth={2} fill="none" strokeLinecap="round"
        {...PL(0.22, 0.40)} />
      {/* Right hand — small skin-tone oval */}
      <motion.ellipse cx={cx+31} cy={shoulY-25} rx={4.5} ry={5.5}
        fill="#fde8cc" stroke={NAVY} strokeWidth={1.5}
        {...SC(0.30)} />

      {/* ══ CLIPBOARD (left hand) — navy outline, no fill ════════════ */}
      <motion.g {...FD(0.28)}>
        <rect x={cx-56} y={hcy+70} width={24} height={30} rx={2}
          fill="none" stroke={NAVY} strokeWidth={2} />
        <rect x={cx-50} y={hcy+66} width={12} height={7} rx={2}
          fill="none" stroke={NAVY} strokeWidth={1.8} />
        <line x1={cx-52} y1={hcy+78} x2={cx-36} y2={hcy+78}
          stroke={NAVY} strokeWidth={1} opacity={0.50} />
        <line x1={cx-52} y1={hcy+84} x2={cx-36} y2={hcy+84}
          stroke={NAVY} strokeWidth={1} opacity={0.50} />
        <line x1={cx-52} y1={hcy+90} x2={cx-40} y2={hcy+90}
          stroke={NAVY} strokeWidth={1} opacity={0.35} />
      </motion.g>

      {/* ══ LEGS ════════════════════════════════════════════════════ */}
      <motion.path d={`M ${cx-10} ${skirtBot} L ${cx-10} ${legBot}`}
        stroke={NAVY} strokeWidth={2} fill="none" strokeLinecap="round"
        {...PL(0.28, 0.36)} />
      <motion.path d={`M ${cx+10} ${skirtBot} L ${cx+10} ${legBot}`}
        stroke={NAVY} strokeWidth={2} fill="none" strokeLinecap="round"
        {...PL(0.30, 0.36)} />

      {/* ══ SHOES ════════════════════════════════════════════════════ */}
      <motion.path d={`M ${cx-10} ${legBot} Q ${cx-19} ${legBot+5} ${cx-19} ${legBot+1}`}
        stroke={NAVY} strokeWidth={2.2} fill="none" strokeLinecap="round"
        {...PL(0.40, 0.14)} />
      <motion.path d={`M ${cx+10} ${legBot} Q ${cx+19} ${legBot+5} ${cx+19} ${legBot+1}`}
        stroke={NAVY} strokeWidth={2.2} fill="none" strokeLinecap="round"
        {...PL(0.42, 0.14)} />
    </g>
  );
}

/* ════════════════════════════════════════════════════════════════
   CHAPTER 1 — Join as an Associate
   ════════════════════════════════════════════════════════════════ */
function Chapter1Scene({ active }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (!active) { setStage(0); return; }
    const ts = [
      setTimeout(() => setStage(1),  150),
      setTimeout(() => setStage(2),  950),
      setTimeout(() => setStage(3), 1850),
      setTimeout(() => setStage(4), 2700),
      setTimeout(() => setStage(5), 3500),
    ];
    return () => ts.forEach(clearTimeout);
  }, [active]);

  // Character geometry: DoodleTeacherPro at cx=280, cy=175
  // hcy = 175-57 = 118; raised hand at (cx+27, hcy-2) = (307, 116)
  const TCX = 280, TCY = 175;

  return (
    <svg viewBox="0 0 680 380" className="fa-svg">
      {/* SVG marker for arrowhead — solid navy triangle */}
      <defs>
        <marker id="ch1-arrow" markerWidth="10" markerHeight="7"
          refX="9" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#1a2e4a" />
        </marker>
      </defs>

      {/* Subtle rule lines */}
      {[110, 195, 280].map((y, i) => (
        <line key={i} x1={30} y1={y} x2={650} y2={y}
          stroke={INK} strokeWidth={0.4} opacity={0.035} />
      ))}

      {/* ── Professional teacher illustration ── */}
      <DoodleTeacherPro cx={TCX} cy={TCY} show={stage >= 1} delay={0} />

      {/* YOU label — 16px below feet (feet at hcy+169 ≈ 287) */}
      <motion.text x={TCX} y={307} textAnchor="middle"
        fontSize={10} fontWeight={700} letterSpacing="0.15em"
        fill="#888" fontFamily="Inter, sans-serif"
        initial={{ opacity: 0 }} animate={{ opacity: stage >= 1 ? 1 : 0 }}
        transition={{ duration: 0.3, delay: 0.92 }}>YOU</motion.text>

      {/* ── Fellowship Code card — vertically centred with character ── */}
      {/* Card y=72, height=118 → centre y=131 ≈ character head+chest centre */}
      <motion.g
        initial={{ y: -90, opacity: 0 }}
        animate={{ y: stage >= 2 ? 0 : -90, opacity: stage >= 2 ? 1 : 0 }}
        transition={{ type: 'spring', stiffness: 240, damping: 22 }}>
        <rect x={388} y={72} width={218} height={118} rx={12}
          fill="white" stroke="#1a2e4a" strokeWidth={2}
          style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.10))' }} />
        <text x={497} y={104} textAnchor="middle" fontSize={8} fill="#94a3b8"
          letterSpacing={1.8} fontFamily="Inter, sans-serif">FELLOWSHIP CODE</text>
        <text x={497} y={147} textAnchor="middle" fontSize={28} fontWeight={900} fill="#1a2e4a"
          fontFamily="Outfit, sans-serif" letterSpacing={1}>A-XXXXX</text>
        <text x={497} y={170} textAnchor="middle" fontSize={9} fill="#94a3b8"
          fontFamily="Inter, sans-serif">Your unique referral code</text>
      </motion.g>

      {/* ── Curved dashed arrow: right shoulder → card left edge ── */}
      {/* Starts near shoulder (310,150), arcs upward, lands at card centre-left (388,128) */}
      <motion.path
        d="M 311 152 C 340 108 372 112 388 128"
        stroke="#1a2e4a" strokeWidth={2} strokeDasharray="6,4"
        fill="none" markerEnd="url(#ch1-arrow)"
        initial={{ opacity: 0 }}
        animate={{ opacity: stage >= 5 ? 1 : 0 }}
        transition={{ duration: 0.55, ease: EASE }}
      />

      {/* ── YOM coins floating up ── */}
      {stage >= 3 && <>
        <DoodleCoin cx={TCX - 5}  cy={TCY + 15} toY={TCY - 70} show delay={0}    label="+30" />
        <DoodleCoin cx={TCX + 28} cy={TCY + 22} toY={TCY - 62} show delay={0.22} label="+10" />
      </>}

      {/* ── Associate badge — centred under teacher (cx=280) ── */}
      {/* Badge x=220, width=120 → centre at 280 ✓ */}
      <DoodleBadge x={220} y={318} label="ASSOCIATE" tier="associate" show={stage >= 4} delay={0} />

      {/* Pill tags — centred as a group at cx=280 */}
      {/* Total pill width: 68+8+80=156; start at 280-78=202 */}
      <motion.g initial={{ y: 14, opacity: 0 }}
        animate={{ y: stage >= 4 ? 0 : 14, opacity: stage >= 4 ? 1 : 0 }}
        transition={{ duration: 0.40, delay: 0.26 }}>
        <rect x={202} y={360} width={68} height={20} rx={10}
          fill="#f1f5f9" stroke="#94a3b8" strokeWidth={1} />
        <text x={236} y={374} textAnchor="middle" fontSize={9} fontWeight={600} fill="#1e293b"
          fontFamily="Inter, sans-serif">Silver Plan</text>
      </motion.g>
      <motion.g initial={{ y: 14, opacity: 0 }}
        animate={{ y: stage >= 4 ? 0 : 14, opacity: stage >= 4 ? 1 : 0 }}
        transition={{ duration: 0.40, delay: 0.44 }}>
        <rect x={278} y={360} width={80} height={20} rx={10}
          fill="#f1f5f9" stroke="#94a3b8" strokeWidth={1} />
        <text x={318} y={374} textAnchor="middle" fontSize={9} fontWeight={600} fill="#1e293b"
          fontFamily="Inter, sans-serif">30-day Trial</text>
      </motion.g>
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════════
   CHAPTER 2 — Refer 10 Educators. Earn 500 YOMs.
   ════════════════════════════════════════════════════════════════ */
const CH2_FLOAT_Y = [0, -4, 0]; // stable ref — prevents animation restart on re-render

function Chapter2Scene({ active }) {
  const [stage, setStage] = useState(0);
  const yomCount = useYOMCounter(stage >= 5, 500);

  useEffect(() => {
    if (!active) { setStage(0); return; }
    const ts = [
      setTimeout(() => setStage(1),  100),
      setTimeout(() => setStage(2),  700),
      setTimeout(() => setStage(3), 1600),
      setTimeout(() => setStage(4), 2300),
      setTimeout(() => setStage(5), 3000),
      setTimeout(() => setStage(6), 3800),
    ];
    return () => ts.forEach(clearTimeout);
  }, [active]);

  const NAVY  = '#1a2e4a';
  const AMBER = '#E8A020';
  const CR    = 20;

  // YOU character — left anchor
  const TCX = 82, TCY = 195;
  const hcy = TCY - 57; // 138

  // Arrow source: spreads along the right torso to reduce "bundle" effect
  const ASX     = TCX + 28; // 110
  const ASY_MID = hcy + 55; // 193 — mid-torso
  const CYS     = [58, 110, 162, 214, 266];
  const CYSMID  = (CYS[0] + CYS[4]) / 2; // 162

  // Each arrow emerges from a slightly different y on the right side of the body
  const srcY = (ty) => ASY_MID + (ty - CYSMID) * 0.22;

  // Col A (nearer) and Col B (farther right)
  const CA = 310, CB = 430;

  const teachers = [
    ...CYS.map(y => ({ cx: CA, cy: y, isB: false })),
    ...CYS.map(y => ({ cx: CB, cy: y, isB: true  })),
  ];

  const YOM = ['+10', '+30', '+60', '+100', '+50', '+30', '+60', '+100', '+30', '+100'];

  // Cubic bezier — source is spread, ends at circle edge
  const mkArrow = (tx, ty) => {
    const sy  = srcY(ty);
    const dx  = tx - ASX, dy = ty - sy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const ex  = tx - (dx / dist) * (CR + 2);
    const ey  = ty - (dy / dist) * (CR + 2);
    const cp1x = ASX + dx * 0.38;
    const cp1y = sy  + dy * 0.04;
    const cp2x = ASX + dx * 0.74;
    const cp2y = ey  + (sy - ey) * 0.04;
    return `M${ASX},${sy} C${cp1x},${cp1y} ${cp2x},${cp2y} ${ex},${ey}`;
  };

  // Badges sit just to the LEFT of each circle face — mirrored symmetry:
  // col A t=0.74 → x≈264 (26px left of col A edge at 290)
  // col B t=0.80 → x≈366 (44px left of col B edge at 410, 36px right of col A edge at 330)
  const mkBadge = (tx, ty, isB) => {
    const sy = srcY(ty);
    const t  = isB ? 0.80 : 0.74;
    return { x: ASX + (tx - ASX) * t, y: sy + (ty - sy) * t - 16 };
  };

  // Progress bar inside card
  const TRACK_W  = 164;
  const progressW = Math.min(Math.round((yomCount / 500) * TRACK_W), TRACK_W);

  return (
    <svg viewBox="0 0 750 390" className="fa-svg">
      <defs>
        <marker id="ch2-arr" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill={NAVY} />
        </marker>
      </defs>

      {/* Subtle rule lines */}
      {[110, 162, 214].map((y, i) => (
        <line key={i} x1={40} y1={y} x2={710} y2={y}
          stroke={NAVY} strokeWidth={0.3} opacity={0.028} />
      ))}

      {/* YOU — DoodleTeacherPro (exact same as Chapter 1) */}
      <DoodleTeacherPro cx={TCX} cy={TCY} show={stage >= 1} delay={0} />
      <motion.text x={TCX} y={TCY + 122} textAnchor="middle"
        fontSize={11} fontWeight={700} letterSpacing="0.15em" fill="#888"
        fontFamily="Inter, sans-serif"
        initial={{ opacity: 0 }} animate={{ opacity: stage >= 1 ? 1 : 0 }}
        transition={{ duration: 0.3, delay: 0.9 }}>YOU</motion.text>

      {/* ARROWS — drawn first so circles render on top (masks any overlap) */}
      {teachers.map((t, i) => (
        <motion.path key={`a${i}`}
          d={mkArrow(t.cx, t.cy)}
          stroke={NAVY} strokeWidth={1.5} strokeDasharray="5,4" fill="none"
          markerEnd="url(#ch2-arr)"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: stage >= 3 ? 1 : 0, opacity: stage >= 3 ? 0.55 : 0 }}
          transition={{ duration: 0.55, ease: EASE, delay: i * 0.07 }}
        />
      ))}

      {/* PULSE RINGS — amber breath animation behind each avatar */}
      {teachers.map((t, i) => (
        <motion.circle key={`p${i}`}
          cx={t.cx} cy={t.cy} r={CR + 6}
          fill="none" stroke={AMBER} strokeWidth={1.2}
          initial={{ scale: 1, opacity: 0 }}
          animate={stage >= 2 ? { scale: [1, 1.55, 1], opacity: [0, 0.35, 0] } : { opacity: 0 }}
          transition={stage >= 2 ? {
            duration: 2.8, repeat: Infinity, ease: 'easeOut', delay: 1.2 + i * 0.2,
          } : { duration: 0.1 }}
        />
      ))}

      {/* CIRCLES — bouncy entrance, on top of arrows */}
      {teachers.map((t, i) => (
        <motion.g key={`t${i}`}
          style={{ transformBox: 'fill-box', transformOrigin: `${t.cx}px ${t.cy}px` }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: stage >= 2 ? 1 : 0, opacity: stage >= 2 ? 1 : 0 }}
          transition={{ ...SP_BOUNCE, delay: 0.4 + i * 0.1 }}>
          <circle cx={t.cx} cy={t.cy} r={CR} stroke={NAVY} strokeWidth={2} fill="#fdf6ee" />
          <circle cx={t.cx - 5} cy={t.cy - 3} r={1.8} fill={NAVY} />
          <circle cx={t.cx + 5} cy={t.cy - 3} r={1.8} fill={NAVY} />
          <path d={`M${t.cx-5},${t.cy+4} Q${t.cx},${t.cy+9} ${t.cx+5},${t.cy+4}`}
            stroke={NAVY} strokeWidth={1.5} fill="none" strokeLinecap="round" />
          <text x={t.cx} y={t.cy + CR + 13} textAnchor="middle"
            fontSize={10} fill="#aaa" fontFamily="Inter, sans-serif">{i + 1}</text>
        </motion.g>
      ))}

      {/* YOM BADGES — clean positions, spring bounce entrance, gentle float */}
      {teachers.map((t, i) => {
        const bp  = mkBadge(t.cx, t.cy, t.isB);
        const lbl = YOM[i];
        const bW  = lbl.length * 7.5 + 14;
        return (
          <motion.g key={`y${i}`}
            style={{ transformBox: 'fill-box', transformOrigin: `${bp.x}px ${bp.y}px` }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale:   stage >= 4 ? 1 : 0,
              opacity: stage >= 4 ? 1 : 0,
              y:       stage >= 4 ? CH2_FLOAT_Y : 0,
            }}
            transition={{
              scale:   { ...SP_BOUNCE, delay: 0.06 + i * 0.08 },
              opacity: { duration: 0.25, delay: 0.06 + i * 0.08 },
              y: stage >= 4 ? {
                duration: 2.4 + i * 0.18, repeat: Infinity,
                ease: 'easeInOut', delay: 0.7 + i * 0.12,
              } : { duration: 0 },
            }}>
            <rect x={bp.x - bW/2} y={bp.y - 9} width={bW} height={18} rx={9}
              fill="white" stroke={AMBER} strokeWidth={1.5}
              style={{ filter: 'drop-shadow(0 2px 5px rgba(232,160,32,0.22))' }} />
            <text x={bp.x} y={bp.y + 4.5} textAnchor="middle"
              fontSize={11} fontWeight={600} fill={AMBER} fontFamily="Inter, sans-serif">{lbl}</text>
          </motion.g>
        );
      })}

      {/* TARGET MET label — above card */}
      <motion.text x={608} y={18} textAnchor="middle"
        fontSize={13} fontWeight={700} letterSpacing="0.1em" fill={AMBER}
        fontFamily="Inter, sans-serif"
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: stage >= 6 ? 1 : 0, scale: stage >= 6 ? 1 : 0.85 }}
        transition={SP_BOUNCE}>TARGET MET ✦</motion.text>

      {/* PROGRESS CARD — bar is the hero, not a number */}
      <motion.g
        initial={{ y: -28, opacity: 0 }}
        animate={{ y: stage >= 5 ? 0 : -28, opacity: stage >= 5 ? 1 : 0 }}
        transition={{ type: 'spring', stiffness: 240, damping: 22 }}>
        <rect x={508} y={26} width={218} height={92} rx={10} fill="white"
          style={{ filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.09))' }} />

        {/* "TARGET: 500 YOMs" label */}
        <text x={617} y={48} textAnchor="middle" fontSize={10} fontWeight={700}
          fill="#bbb" letterSpacing="0.12em" fontFamily="Inter, sans-serif">TARGET: 500 YOMs</text>

        {/* Progress bar track */}
        <rect x={522} y={58} width={TRACK_W} height={18} rx={9} fill="#f1f5f9" />

        {/* Animated amber fill */}
        <rect x={522} y={58} width={progressW} height={18} rx={9} fill={AMBER} />

        {/* Tick marks at 25 / 50 / 75% */}
        {[0.25, 0.50, 0.75].map((pct, i) => (
          <line key={i}
            x1={522 + TRACK_W * pct} y1={58}
            x2={522 + TRACK_W * pct} y2={76}
            stroke="white" strokeWidth={1.5} opacity={0.5} />
        ))}

        {/* YOM count label below bar */}
        <text x={617} y={96} textAnchor="middle" fontSize={11} fontWeight={700}
          fill={AMBER} fontFamily="Outfit, sans-serif">{yomCount} / 500 YOMs</text>
      </motion.g>

      {/* 30-DAY WINDOW badge — navy + amber, no red */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: stage >= 5 ? 1 : 0 }}
        transition={{ duration: 0.35, delay: 0.3 }}>
        <rect x={28} y={348} width={178} height={28} rx={14}
          fill="white" stroke={AMBER} strokeWidth={1.5} />
        <circle cx={47} cy={362} r={7} stroke={NAVY} strokeWidth={1.5} fill="none" />
        <line x1={47} y1={356} x2={47} y2={362} stroke={NAVY} strokeWidth={1.5} strokeLinecap="round" />
        <line x1={47} y1={362} x2={52} y2={365} stroke={NAVY} strokeWidth={1.5} strokeLinecap="round" />
        <text x={120} y={367} textAnchor="middle" fontSize={12} fontWeight={600}
          fill={NAVY} letterSpacing="0.08em" fontFamily="Inter, sans-serif">30-DAY WINDOW</text>
      </motion.g>

      {/* SPARKLES — uniform perimeter: 4 top, 2 right, 2 bottom */}
      {[[58, 22], [225, 18], [390, 18], [478, 26], [722, 155], [722, 298], [482, 365], [270, 358]].map(([sx, sy], i) => (
        <DoodleSparkle key={i} cx={sx} cy={sy} show={stage >= 6} delay={i * 0.09}
          color={AMBER} size={0.85} />
      ))}
    </svg>
  );
}

/* ── MiniTeacher — compact teacher doodle for the 8/10 scene ─── */
function MiniTeacher({ cx, cy, show, delay = 0 }) {
  const NAVY = '#1a2e4a';
  const HAIR = '#3b2a1a';
  const SKIN = '#fdf6ee';
  const bodyBY = cy   - 18;
  const bodyTY = bodyBY - 22;
  const headCY = bodyTY - 14;
  const bunCY  = headCY - 14;
  return (
    <motion.g
      style={{ transformBox: 'fill-box', transformOrigin: `${cx}px ${cy}px` }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: show ? 1 : 0, opacity: show ? 1 : 0 }}
      transition={{ ...SP_BOUNCE, delay }}>
      <circle  cx={cx} cy={bunCY}  r={5.5} fill={HAIR} />
      <ellipse cx={cx} cy={headCY} rx={8} ry={9} fill={SKIN} stroke={NAVY} strokeWidth={1.5} />
      <circle cx={cx - 3} cy={headCY - 1} r={1.3} fill={NAVY} />
      <circle cx={cx + 3} cy={headCY - 1} r={1.3} fill={NAVY} />
      <path d={`M${cx-3},${headCY+5} Q${cx},${headCY+8} ${cx+3},${headCY+5}`}
        stroke={NAVY} strokeWidth={1.2} fill="none" strokeLinecap="round" />
      <path d={`M${cx-5},${bodyTY} L${cx-13},${bodyBY} L${cx+13},${bodyBY} L${cx+5},${bodyTY} Z`}
        fill={NAVY} />
      <line x1={cx-5} y1={bodyBY} x2={cx-5} y2={cy} stroke={NAVY} strokeWidth={1.8} strokeLinecap="round" />
      <line x1={cx+5} y1={bodyBY} x2={cx+5} y2={cy} stroke={NAVY} strokeWidth={1.8} strokeLinecap="round" />
    </motion.g>
  );
}

/* ════════════════════════════════════════════════════════════════
   CHAPTER 3 — Silver plan. Yours for life.
   Story: 10 associates appear → 8 win ✓ → YOU become FELLOW → rewards
   ════════════════════════════════════════════════════════════════ */
function Chapter3Scene({ active }) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (!active) { setStage(0); return; }
    const ts = [
      setTimeout(() => setStage(1),  100),   // title + 10 mini teachers
      setTimeout(() => setStage(2), 1000),   // checkmarks + pulse rings
      setTimeout(() => setStage(3), 2400),   // "8/10" result banner
      setTimeout(() => setStage(4), 3200),   // teachers fade + YOU appears
      setTimeout(() => setStage(5), 3900),   // FELLOW badge
      setTimeout(() => setStage(6), 4500),   // Silver card
      setTimeout(() => setStage(7), 5100),   // Two lower reward cards
      setTimeout(() => setStage(8), 5600),   // Bottom bar + sparkles
    ];
    return () => ts.forEach(clearTimeout);
  }, [active]);

  const NAVY  = '#1a2e4a';
  const AMBER = '#E8A020';

  // 10 mini teachers, 68px spacing, centered in 750px viewBox
  // MiniTeacher geometry (cy=252): bodyBY=234, bodyTY=212, headCY=198, bunCY=184, bunTop≈178
  const associates = Array.from({ length: 10 }, (_, i) => ({
    cx: 69 + i * 68,
    cy: 252,
    won: i < 8,
  }));
  const HEAD_CY = 198;
  const BUN_TOP = 178;

  return (
    <svg viewBox="0 0 750 390" className="fa-svg">

      {/* ── ACT 1: 10 mini teacher doodles ─────────────────── */}

      <motion.text x={375} y={48} textAnchor="middle"
        fontSize={11} fontWeight={700} letterSpacing="0.18em" fill="#94a3b8"
        fontFamily="Inter, sans-serif"
        initial={{ opacity: 0 }}
        animate={{ opacity: stage >= 1 && stage < 4 ? 1 : 0 }}
        transition={{ duration: 0.3 }}>YOUR 10 ASSOCIATES</motion.text>

      <motion.text x={375} y={74} textAnchor="middle"
        fontSize={9} fill="#ccc" fontFamily="Inter, sans-serif"
        initial={{ opacity: 0 }}
        animate={{ opacity: stage >= 1 && stage < 4 ? 1 : 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}>each earns you YOMs when they achieve the goal</motion.text>

      {/* Pulse rings (winners, behind teachers) */}
      {associates.map((a, i) => a.won && (
        <motion.circle key={`pr${i}`}
          cx={a.cx} cy={HEAD_CY} r={14}
          fill="none" stroke={AMBER} strokeWidth={1.2}
          animate={stage >= 2 && stage < 4
            ? { scale: [1, 1.8, 1], opacity: [0, 0.5, 0] }
            : { opacity: 0, scale: 1 }}
          transition={stage >= 2 && stage < 4
            ? { duration: 2.2, repeat: Infinity, ease: 'easeOut', delay: 0.5 + i * 0.11 }
            : { duration: 0.2 }}
        />
      ))}

      {/* Mini teachers — losers dimmed at stage 2+ */}
      {associates.map((a, i) => (
        <motion.g key={`dw${i}`}
          animate={{ opacity: !a.won && stage >= 2 && stage < 4 ? 0.32 : 1 }}
          transition={{ duration: 0.4 }}>
          <MiniTeacher cx={a.cx} cy={a.cy}
            show={stage >= 1 && stage < 4}
            delay={0.04 + i * 0.07} />
        </motion.g>
      ))}

      {/* Checkmarks above bun (winners) */}
      {associates.map((a, i) => a.won && (
        <motion.path key={`ck${i}`}
          d={`M${a.cx-5},${BUN_TOP-11} L${a.cx},${BUN_TOP-5} L${a.cx+9},${BUN_TOP-18}`}
          stroke={AMBER} strokeWidth={2.5} fill="none"
          strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: stage >= 2 && stage < 4 ? 1 : 0, opacity: stage >= 2 && stage < 4 ? 1 : 0 }}
          transition={{ duration: 0.3, ease: EASE, delay: stage >= 2 ? 0.28 + i * 0.09 : 0 }}
        />
      ))}

      {/* Crosses above bun (losers) */}
      {associates.map((a, i) => !a.won && (
        <motion.g key={`xm${i}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: stage >= 2 && stage < 4 ? 0.5 : 0 }}
          transition={{ duration: 0.3, delay: stage >= 2 ? 0.38 + i * 0.09 : 0 }}>
          <line x1={a.cx-5} y1={BUN_TOP-18} x2={a.cx+5} y2={BUN_TOP-8}
            stroke="#aaa" strokeWidth={2} strokeLinecap="round" />
          <line x1={a.cx+5} y1={BUN_TOP-18} x2={a.cx-5} y2={BUN_TOP-8}
            stroke="#aaa" strokeWidth={2} strokeLinecap="round" />
        </motion.g>
      ))}

      {/* Number labels */}
      {associates.map((a, i) => (
        <motion.text key={`nl${i}`} x={a.cx} y={266} textAnchor="middle"
          fontSize={8.5} fill="#ccc" fontFamily="Inter, sans-serif"
          initial={{ opacity: 0 }}
          animate={{ opacity: stage >= 1 && stage < 4 ? 1 : 0 }}
          transition={{ duration: 0.25, delay: 0.1 + i * 0.06 }}>{i + 1}</motion.text>
      ))}

      {/* "8 out of 10 hit the goal ✓" result banner */}
      <motion.g
        style={{ transformBox: 'fill-box', transformOrigin: '375px 297px' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: stage >= 3 && stage < 4 ? 1 : 0, opacity: stage >= 3 && stage < 4 ? 1 : 0 }}
        transition={{ ...SP_BOUNCE }}>
        <rect x={225} y={280} width={300} height={50} rx={12}
          fill="white" stroke={AMBER} strokeWidth={2}
          style={{ filter: 'drop-shadow(0 4px 16px rgba(232,160,32,0.28))' }} />
        <text x={375} y={300} textAnchor="middle" fontSize={9.5} fontWeight={700}
          fill="#94a3b8" letterSpacing="0.16em" fontFamily="Inter, sans-serif">RESULT</text>
        <text x={375} y={319} textAnchor="middle" fontSize={14} fontWeight={800}
          fill={AMBER} fontFamily="Outfit, sans-serif">8 out of 10 hit the goal ✓</text>
      </motion.g>

      {/* ── ACT 2: YOU → FELLOW ─────────────────────────────── */}

      {/* DoodleTeacherPro — same character as Chapter 1 */}
      <DoodleTeacherPro cx={148} cy={195} show={stage >= 4} delay={0.1} />

      <motion.text x={148} y={332} textAnchor="middle"
        fontSize={10} fontWeight={700} letterSpacing="0.15em" fill="#94a3b8"
        fontFamily="Inter, sans-serif"
        initial={{ opacity: 0 }} animate={{ opacity: stage >= 4 ? 1 : 0 }}
        transition={{ duration: 0.3, delay: 0.85 }}>YOU</motion.text>

      <motion.text x={148} y={66} textAnchor="middle"
        fontSize={13} fontWeight={800} fill={NAVY} fontFamily="Outfit, sans-serif"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: stage >= 4 ? 1 : 0, y: stage >= 4 ? 0 : 10 }}
        transition={{ duration: 0.4, delay: 0.5 }}>You're now a Fellow!</motion.text>

      {/* Graduation cap — positioned over DoodleTeacherPro bun (hcy=138, ry=19, head top=119) */}
      <motion.g
        style={{ transformBox: 'fill-box', transformOrigin: '148px 96px' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: stage >= 4 ? 1 : 0, opacity: stage >= 4 ? 1 : 0 }}
        transition={{ ...SP, delay: 0.62 }}>
        <rect x={133} y={74}  width={30} height={26} rx={2} fill={NAVY} />
        <rect x={130} y={98}  width={36} height={4}  rx={1} fill={NAVY} />
        <line x1={126} y1={102} x2={170} y2={102} stroke={AMBER} strokeWidth={2.5} strokeLinecap="round" />
        <line x1={166} y1={90}  x2={177} y2={101}   stroke={AMBER} strokeWidth={1.8} strokeLinecap="round" />
        <circle cx={177} cy={104} r={3.5} fill={AMBER} />
      </motion.g>

      {/* FELLOW badge */}
      <motion.g
        style={{ transformBox: 'fill-box', transformOrigin: '148px 363px' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: stage >= 5 ? 1 : 0, opacity: stage >= 5 ? 1 : 0 }}
        transition={{ ...SP_BOUNCE, delay: 0.06 }}>
        <rect x={78} y={344} width={140} height={38} rx={9}
          fill={NAVY} stroke={AMBER} strokeWidth={2}
          style={{ filter: 'drop-shadow(0 4px 14px rgba(26,46,74,0.38))' }} />
        <text x={148} y={369} textAnchor="middle" fontSize={16} fontWeight={900}
          fill={AMBER} letterSpacing="0.18em" fontFamily="Outfit, sans-serif">FELLOW</text>
      </motion.g>

      {/* ── ACT 3: Reward grid (right side, clean 3-row layout) ─ */}

      {/* ① Silver card — full width, slides in from right */}
      <motion.g
        initial={{ x: 130, opacity: 0 }}
        animate={{ x: stage >= 6 ? 0 : 130, opacity: stage >= 6 ? 1 : 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 26 }}>
        <rect x={330} y={38} width={396} height={100} rx={10}
          fill={NAVY} stroke={AMBER} strokeWidth={2}
          style={{ filter: 'drop-shadow(0 6px 20px rgba(26,46,74,0.28))' }} />
        <text x={350} y={63} fontSize={8} fontWeight={700} fill="#94a3b8"
          fontFamily="Inter, sans-serif" letterSpacing="0.18em">SUBSCRIPTION</text>
        <motion.circle cx={354} cy={96} r={10}
          stroke={AMBER} strokeWidth={2} fill="none"
          initial={{ pathLength: 0 }} animate={{ pathLength: stage >= 6 ? 1 : 0 }}
          transition={{ duration: 0.5, ease: EASE, delay: 0.38 }} />
        <motion.circle cx={370} cy={96} r={10}
          stroke={AMBER} strokeWidth={2} fill="none"
          initial={{ pathLength: 0 }} animate={{ pathLength: stage >= 6 ? 1 : 0 }}
          transition={{ duration: 0.5, ease: EASE, delay: 0.50 }} />
        <text x={582} y={108} textAnchor="middle" fontSize={32} fontWeight={900} fill={AMBER}
          fontFamily="Outfit, sans-serif">Silver</text>
        <text x={582} y={126} textAnchor="middle" fontSize={11} fill="#cbd5e1"
          fontFamily="Inter, sans-serif">Lifetime Access</text>
      </motion.g>

      {/* ② Left lower card — 250 YOMs */}
      <motion.g
        style={{ transformBox: 'fill-box', transformOrigin: '425px 210px' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: stage >= 7 ? 1 : 0, opacity: stage >= 7 ? 1 : 0 }}
        transition={{ ...SP_BOUNCE, delay: 0 }}>
        <rect x={330} y={154} width={191} height={108} rx={10}
          fill="white" stroke="#e2e8f0" strokeWidth={1.5}
          style={{ filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.06))' }} />
        <text x={425} y={184} textAnchor="middle" fontSize={9} fontWeight={600}
          fill="#94a3b8" fontFamily="Inter, sans-serif" letterSpacing="0.1em">REDEEM UP TO</text>
        <text x={425} y={220} textAnchor="middle" fontSize={26} fontWeight={900} fill={AMBER}
          fontFamily="Outfit, sans-serif">250 YOMs</text>
        <text x={425} y={238} textAnchor="middle" fontSize={9} fill="#94a3b8"
          fontFamily="Inter, sans-serif">in the store</text>
      </motion.g>

      {/* ③ Right lower card — +100 YOM BONUS */}
      <motion.g
        style={{ transformBox: 'fill-box', transformOrigin: '629px 210px' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: stage >= 7 ? 1 : 0, opacity: stage >= 7 ? 1 : 0 }}
        transition={{ ...SP_BOUNCE, delay: 0.12 }}>
        <rect x={533} y={154} width={193} height={108} rx={10}
          fill={AMBER} stroke="#b45309" strokeWidth={1.5}
          style={{ filter: 'drop-shadow(0 3px 12px rgba(232,160,32,0.38))' }} />
        <text x={629} y={196} textAnchor="middle" fontSize={22} fontWeight={900} fill={NAVY}
          fontFamily="Outfit, sans-serif">+100 YOM</text>
        <text x={629} y={218} textAnchor="middle" fontSize={14} fontWeight={800} fill={NAVY}
          fontFamily="Outfit, sans-serif">BONUS</text>
        <text x={629} y={240} textAnchor="middle" fontSize={8.5} fill="#78350f"
          fontFamily="Inter, sans-serif">per associate win</text>
      </motion.g>

      {/* ④ Bottom bar — Silver plan note */}
      <motion.g
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: stage >= 8 ? 1 : 0, y: stage >= 8 ? 0 : 8 }}
        transition={{ duration: 0.45, delay: 0.1 }}>
        <rect x={330} y={278} width={396} height={44} rx={10}
          fill="white" stroke="#e2e8f0" strokeWidth={1.5} />
        <text x={528} y={305} textAnchor="middle" fontSize={12} fontWeight={700} fill={NAVY}
          fontFamily="Outfit, sans-serif">✦  Silver plan — yours for life</text>
      </motion.g>

      {/* Sparkles — uniform perimeter */}
      {[[60, 24], [245, 18], [460, 20], [700, 22], [722, 185], [722, 316], [480, 368], [260, 368]].map(([sx, sy], i) => (
        <DoodleSparkle key={i} cx={sx} cy={sy} show={stage >= 8} delay={i * 0.09}
          color={AMBER} size={0.85} />
      ))}
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════════
   CHAPTER 4 — Distinguished Fellow
   YOU → 5 Fellow smiley circles (fan) → 3 win (tick + glow) →
   each winner's 4 associate smileys light up → DIST. FELLOW + Platinum
   ════════════════════════════════════════════════════════════════ */
const CH4_FX  = [270, 360, 450, 540, 630];
const CH4_WIN = new Set([1, 2, 3]);
const CH4_FCY = 148;   // fellow circle centre y
const CH4_BAR = 82;    // horizontal connector bar y
const CH4_SRC = { x: 196, y: 160 }; // connection point on YOU (right torso)

function Chapter4Scene({ active }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (!active) { setStage(0); return; }
    const ts = [
      setTimeout(() => setStage(1),  150),  // YOU
      setTimeout(() => setStage(2),  750),  // fellow circles appear
      setTimeout(() => setStage(3), 1450),  // fan lines draw
      setTimeout(() => setStage(4), 2200),  // associate smileys appear
      setTimeout(() => setStage(5), 3050),  // 3 winners: tick + glow + associates amber
      setTimeout(() => setStage(6), 3900),  // DIST. FELLOW + crown
      setTimeout(() => setStage(7), 4700),  // Platinum card
    ];
    return () => ts.forEach(clearTimeout);
  }, [active]);

  const NAVY   = '#1a2e4a';
  const AMBER  = '#E8A020';
  const isDist = stage >= 6;

  return (
    <svg viewBox="0 0 750 390" className="fa-svg">

      {/* Subtle grid lines */}
      {[140, 200, 255].map((y, i) => (
        <line key={i} x1={40} y1={y} x2={710} y2={y}
          stroke={INK} strokeWidth={0.4} opacity={0.030} />
      ))}

      {/* ── YOU (left) ─────────────────────────────────────────── */}
      <DoodleTeacherPro cx={115} cy={210} show={stage >= 1} delay={0} />

      <motion.text x={115} y={338} textAnchor="middle"
        fontSize={10} fontWeight={700} letterSpacing="0.15em" fill="#94a3b8"
        fontFamily="Inter, sans-serif"
        initial={{ opacity: 0 }} animate={{ opacity: stage >= 1 ? 1 : 0 }}
        transition={{ duration: 0.3, delay: 0.85 }}>YOU</motion.text>

      {/* FELLOW → DIST. FELLOW badge */}
      <AnimatePresence mode="wait">
        <motion.g key={isDist ? 'dist' : 'fellow'}
          style={{ transformBox: 'fill-box', transformOrigin: '115px 366px' }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ ...SP_BOUNCE }}>
          <rect x={isDist ? 29 : 45} y={348}
            width={isDist ? 172 : 140} height={34} rx={9}
            fill={NAVY} stroke={isDist ? '#cbd5e1' : AMBER} strokeWidth={2}
            style={{ filter: 'drop-shadow(0 4px 14px rgba(26,46,74,0.38))' }} />
          <text x={115} y={370} textAnchor="middle"
            fontSize={isDist ? 12 : 14} fontWeight={900}
            fill={isDist ? '#e2e8f0' : AMBER}
            letterSpacing="0.14em" fontFamily="Outfit, sans-serif">
            {isDist ? 'DIST. FELLOW' : 'FELLOW'}
          </text>
        </motion.g>
      </AnimatePresence>

      {/* Distinguished glow ring + crown */}
      <motion.circle cx={115} cy={153} r={70}
        fill={AMBER} fillOpacity={0.07}
        stroke={AMBER} strokeWidth={1.5} strokeOpacity={0.25}
        style={{ transformBox: 'fill-box', transformOrigin: '115px 153px' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: isDist ? 1 : 0, opacity: isDist ? 1 : 0 }}
        transition={{ ...SP_BOUNCE, delay: 0.08 }} />
      <motion.g
        style={{ transformBox: 'fill-box', transformOrigin: '115px 114px' }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: isDist ? 1 : 0, opacity: isDist ? 1 : 0 }}
        transition={{ ...SP_BOUNCE, delay: 0.28 }}>
        <path d="M97,122 L103,106 L115,115 L127,106 L133,122 Z"
          fill="#fef3c7" stroke={AMBER} strokeWidth={1.8}
          strokeLinejoin="round" strokeLinecap="round" />
      </motion.g>

      {/* Divider */}
      <motion.line x1={246} y1={26} x2={246} y2={362}
        stroke={INK} strokeWidth={0.5} strokeOpacity={0.06}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }} />

      {/* "YOUR FELLOWS" header */}
      <motion.text x={455} y={34} textAnchor="middle"
        fontSize={9} fontWeight={700} letterSpacing="0.22em" fill="#94a3b8"
        fontFamily="Inter, sans-serif"
        initial={{ opacity: 0 }} animate={{ opacity: stage >= 2 ? 1 : 0 }}
        transition={{ duration: 0.3 }}>YOUR FELLOWS</motion.text>

      {/* ── Org-chart connectors: curved arm → bar → drops ─────── */}

      {/* Curved arm from YOU raised hand → bar start at divider */}
      <motion.path
        d={`M170,148 C215,115 240,${CH4_BAR} 246,${CH4_BAR}`}
        stroke="#94a3b8" strokeWidth={1} strokeOpacity={0.28}
        fill="none" strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: stage >= 3 ? 1 : 0, opacity: stage >= 3 ? 1 : 0 }}
        transition={{ duration: 0.42, ease: EASE }}
      />

      {/* Horizontal branch bar spanning all fellows */}
      <motion.path
        d={`M246,${CH4_BAR} L${CH4_FX[4] + 26},${CH4_BAR}`}
        stroke="#94a3b8" strokeWidth={0.9} strokeOpacity={0.22}
        fill="none" strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: stage >= 3 ? 1 : 0, opacity: stage >= 3 ? 1 : 0 }}
        transition={{ duration: 0.52, ease: EASE, delay: 0.28 }}
      />

      {/* Vertical drops from bar down to each fellow circle */}
      {CH4_FX.map((fcx, i) => {
        const win = CH4_WIN.has(i) && stage >= 5;
        return (
          <motion.path key={`drop${i}`}
            d={`M${fcx},${CH4_BAR} L${fcx},${CH4_FCY - 24}`}
            stroke={win ? AMBER : '#94a3b8'}
            strokeWidth={win ? 1.5 : 0.9}
            strokeOpacity={win ? 0.70 : 0.25}
            fill="none" strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: stage >= 3 ? 1 : 0, opacity: stage >= 3 ? 1 : 0 }}
            transition={{ duration: 0.30, ease: EASE, delay: 0.46 + i * 0.07 }}
          />
        );
      })}

      {/* ── Fellow smiley circles + associate grid ──────────────── */}
      {CH4_FX.map((fcx, i) => {
        const fcy    = CH4_FCY;
        const win    = CH4_WIN.has(i);
        const showWin = win && stage >= 5;

        return (
          <g key={`f${i}`}>

            {/* Amber glow ring for winners */}
            <motion.circle cx={fcx} cy={fcy} r={30}
              fill={AMBER} fillOpacity={0.10}
              stroke={AMBER} strokeWidth={1.5} strokeOpacity={0.55}
              style={{ transformBox: 'fill-box', transformOrigin: `${fcx}px ${fcy}px` }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: showWin ? 1 : 0, opacity: showWin ? 1 : 0 }}
              transition={{ ...SP, delay: 0.06 + i * 0.1 }} />

            {/* Fellow smiley circle */}
            <motion.g
              style={{ transformBox: 'fill-box', transformOrigin: `${fcx}px ${fcy}px` }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: stage >= 2 ? 1 : 0, opacity: stage >= 2 ? 1 : 0 }}
              transition={{ ...SP_BOUNCE, delay: 0.1 + i * 0.11 }}>
              <circle cx={fcx} cy={fcy} r={22}
                fill={showWin ? '#fef9e7' : '#f8fafc'}
                stroke={showWin ? AMBER : NAVY} strokeWidth={2} />
              <circle cx={fcx - 7} cy={fcy - 4} r={2.2} fill={NAVY} />
              <circle cx={fcx + 7} cy={fcy - 4} r={2.2} fill={NAVY} />
              <path d={`M${fcx-7},${fcy+5} Q${fcx},${fcy+13} ${fcx+7},${fcy+5}`}
                stroke={NAVY} strokeWidth={1.8} fill="none" strokeLinecap="round" />
            </motion.g>

            {/* FELLOW label */}
            <motion.text x={fcx} y={fcy + 36} textAnchor="middle"
              fontSize={7} fontWeight={700} letterSpacing="0.12em"
              fill={showWin ? AMBER : '#94a3b8'} fontFamily="Inter, sans-serif"
              initial={{ opacity: 0 }}
              animate={{ opacity: stage >= 2 ? 1 : 0 }}
              transition={{ duration: 0.25, delay: 0.28 + i * 0.11 }}>FELLOW</motion.text>

            {/* Tick ✓ above winner circles */}
            <motion.path
              d={`M${fcx-8},${fcy-28} L${fcx-2},${fcy-21} L${fcx+10},${fcy-37}`}
              stroke={AMBER} strokeWidth={2.5} fill="none"
              strokeLinecap="round" strokeLinejoin="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: showWin ? 1 : 0, opacity: showWin ? 1 : 0 }}
              transition={{ duration: 0.28, ease: EASE, delay: 0.05 + i * 0.1 }} />

            {/* 4 associate smiley circles in 2×2 grid */}
            {[[-13, 62], [13, 62], [-13, 84], [13, 84]].map(([dx, dy], j) => {
              const assocWon = win && j < 3; // 3-of-4 associates achieved for winner fellows
              const isAmber  = assocWon && stage >= 5;
              return (
                <g key={`a${j}`}>
                  {/* Small smiley circle */}
                  <motion.g
                    style={{ transformBox: 'fill-box', transformOrigin: `${fcx+dx}px ${fcy+dy}px` }}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                      scale:   stage >= 4 ? 1 : 0,
                      opacity: stage >= 4 ? (win ? 1 : 0.35) : 0,
                    }}
                    transition={{ ...SP, delay: 0.06 + i * 0.09 + j * 0.07 }}>
                    <circle cx={fcx+dx} cy={fcy+dy} r={9}
                      fill={isAmber ? '#fef9e7' : '#f8fafc'}
                      stroke={isAmber ? AMBER : '#94a3b8'} strokeWidth={1.2} />
                    <circle cx={fcx+dx-3} cy={fcy+dy-3} r={1.1} fill={NAVY} />
                    <circle cx={fcx+dx+3} cy={fcy+dy-3} r={1.1} fill={NAVY} />
                    <path d={`M${fcx+dx-3},${fcy+dy+2} Q${fcx+dx},${fcy+dy+5} ${fcx+dx+3},${fcy+dy+2}`}
                      stroke={NAVY} strokeWidth={0.9} fill="none" strokeLinecap="round" />
                  </motion.g>

                  {/* Mini tick above each winning associate */}
                  <motion.path
                    d={`M${fcx+dx-4},${fcy+dy-14} L${fcx+dx},${fcy+dy-9} L${fcx+dx+5},${fcy+dy-18}`}
                    stroke={AMBER} strokeWidth={1.5} fill="none"
                    strokeLinecap="round" strokeLinejoin="round"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{
                      pathLength: isAmber ? 1 : 0,
                      opacity:    isAmber ? 1 : 0,
                    }}
                    transition={{ duration: 0.22, ease: EASE, delay: 0.18 + i * 0.09 + j * 0.07 }} />
                </g>
              );
            })}
          </g>
        );
      })}

      {/* ── Platinum achievement — clean card, bottom of scene ────── */}
      <AnimatePresence>
        {stage >= 7 && (
          <motion.g
            initial={{ y: 22, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 14, opacity: 0 }}
            transition={{ ...SP }}>
            {/* Card background */}
            <rect x={254} y={258} width={482} height={100} rx={12}
              fill="#00224D" stroke="rgba(255,255,255,0.10)" strokeWidth={1} />
            {/* Top amber accent line */}
            <rect x={254} y={258} width={482} height={3} rx={1.5}
              fill={AMBER} opacity={0.75} />
            {/* Label */}
            <text x={495} y={281} textAnchor="middle"
              fontSize={7.5} fontWeight={700} fill="rgba(255,255,255,0.38)"
              letterSpacing="0.22em" fontFamily="Inter, sans-serif">DISTINGUISHED FELLOW</text>
            {/* Main text */}
            <text x={495} y={314} textAnchor="middle"
              fontSize={24} fontWeight={900} fill="#f0f6ff"
              fontFamily="Outfit, sans-serif">Platinum ∞ Lifetime</text>
            {/* Sub-line */}
            <text x={495} y={337} textAnchor="middle"
              fontSize={9.5} fontWeight={600} fill={AMBER}
              letterSpacing="0.06em" fontFamily="Inter, sans-serif">1,000 YOMs redeemable · Gold for life</text>
          </motion.g>
        )}
      </AnimatePresence>

    </svg>
  );
}

/* ════════════════════════════════════════════════════════════════
   ANIMATED CAPTION BLOCK — cinematic left-panel text
   ════════════════════════════════════════════════════════════════ */
function AnimatedCaptionBlock({ id, headline, body, tags }) {
  const [go, setGo] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGo(true), 60);
    return () => clearTimeout(t);
  }, []); // remounts on chapter change → re-triggers

  const cine = (d) => ({
    initial:    { opacity: 0, y: 12, filter: 'blur(5px)' },
    animate:    go ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {},
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: d },
  });

  return (
    <div className="fa-left">
      <motion.span className="fa-chapter-eyebrow" {...cine(0)}>
        Chapter {id}
      </motion.span>
      <div className="fa-headline">
        {headline.map((line, i) => (
          <motion.span key={i} className="fa-headline-line" {...cine(0.06 + i * 0.14)}>
            {line}
          </motion.span>
        ))}
      </div>
      <motion.p className="fa-body-text" {...cine(0.06 + headline.length * 0.14 + 0.12)}>
        {body}
      </motion.p>
      <motion.div className="fa-tags" {...cine(0.06 + headline.length * 0.14 + 0.30)}>
        {tags.map((t, i) => <span key={i} className="fa-tag">{t}</span>)}
      </motion.div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   CHAPTERS DATA
   ════════════════════════════════════════════════════════════════ */
const CHAPTERS = [
  {
    id: '01', label: 'Join', component: Chapter1Scene,
    headline: ['Your code,', 'your circle.'],
    body: 'Your referral link is live the moment you sign up. Every paid plan your contact joins earns you +10 to +100 YOMs — permanently recorded, no ceiling.',
    tags: ['Free to Join', 'Code: A-XXXXX', '30-day Silver Trial'],
  },
  {
    id: '02', label: 'Refer', component: Chapter2Scene,
    headline: ['Refer ten.', 'Hit 500 YOMs.'],
    body: 'Bring in 10 paid educators within 30 days. YOM value scales with their plan. Miss the window? Your target resets — but your impact record stays forever.',
    tags: ['10 Paid Referrals', '500 YOM Target', '30-Day Window'],
  },
  {
    id: '03', label: 'Rise', component: Chapter3Scene,
    headline: ['Silver plan.', 'Yours for life.'],
    body: '8 of your 10 associates upgrade — you become a Fellow. Redeem up to 250 YOMs for vouchers and store credits. Each associate win also earns you +100 bonus.',
    tags: ['Silver Lifetime', '250 YOMs Redeemable', '+100 Bonus per Win'],
  },
  {
    id: '04', label: 'Lead', component: Chapter4Scene,
    headline: ['Your network.', 'Your legacy.'],
    body: '8 of your Fellows hit their targets — Senior Fellow, Gold for life. 8 of 20 Fellows rise again — Distinguished Fellow, Platinum for life with 1,000 YOMs.',
    tags: ['8/10 Fellows → Senior', '8/20 → Distinguished', 'Up to 1,000 YOMs'],
  },
];

const SLIDE_VARIANTS = {
  enter:  (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 280, damping: 26 } },
  exit:   (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0, transition: { duration: 0.18 } }),
};

/* ════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════ */

// Time to stay on each chapter: last stage ms + comfortable reading time
const CHAPTER_DURATION = [6500, 7000, 8500, 7500];

export default function FellowshipAnimation() {
  const [chapter,   setChapter]   = useState(0);
  const [direction, setDirection] = useState(1);
  const autoRef = useRef(null);
  const ref     = useRef(null);
  const inView  = useInView(ref, { once: false, amount: 0.20 });

  // Reset to chapter 0 when scrolled out of view
  useEffect(() => {
    if (!inView) {
      clearTimeout(autoRef.current);
      setChapter(0);
    }
  }, [inView]);

  // Auto-advance: schedule next chapter whenever chapter or inView changes
  useEffect(() => {
    if (!inView) return;
    clearTimeout(autoRef.current);
    autoRef.current = setTimeout(() => {
      setDirection(1);
      setChapter(prev => (prev + 1) % CHAPTERS.length);
    }, CHAPTER_DURATION[chapter]);
    return () => clearTimeout(autoRef.current);
  }, [chapter, inView]);

  // Manual jump — resets auto timer via chapter dep above
  const goTo = (index) => {
    setDirection(index > chapter ? 1 : -1);
    setChapter(index);
  };

  const ch             = CHAPTERS[chapter];
  const SceneComponent = ch.component;

  return (
    <section className="fa-section" ref={ref}>
      <div className="fa-outer">

        {/* Navigator — click any chip to jump */}
        <div className="fa-nav" role="tablist">
          {CHAPTERS.map((c, i) => (
            <React.Fragment key={i}>
              <button
                className={`fa-nav-step ${i === chapter ? 'fa-nav-step--active' : ''}`}
                onClick={() => goTo(i)} role="tab" aria-selected={i === chapter}>
                <span className="fa-nav-chip">{c.id}</span>
                <span className="fa-nav-label">{c.label}</span>
              </button>
              {i < CHAPTERS.length - 1 && <span className="fa-nav-connector" aria-hidden="true" />}
            </React.Fragment>
          ))}
        </div>

        {/* Chapter card */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div key={chapter} className="fa-card"
            custom={direction}
            variants={SLIDE_VARIANTS}
            initial="enter" animate="center" exit="exit">

            <AnimatedCaptionBlock
              id={ch.id}
              headline={ch.headline}
              body={ch.body}
              tags={ch.tags}
            />

            <div className="fa-right">
              <SceneComponent active={inView} />
            </div>

          </motion.div>
        </AnimatePresence>

        {/* Auto-advance progress bar + clickable dots */}
        <div className="fa-auto-bar">
          {/* Thin timer bar */}
          <div className="fa-timer-track">
            <motion.div
              key={`timer-${chapter}`}
              className="fa-timer-fill"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: CHAPTER_DURATION[chapter] / 1000, ease: 'linear' }}
            />
          </div>
          {/* Dot indicators */}
          <div className="fa-progress-dots">
            {CHAPTERS.map((_, i) => (
              <span key={i} className={`fa-dot ${i === chapter ? 'fa-dot--active' : ''}`}
                onClick={() => goTo(i)} style={{ cursor: 'pointer' }} />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
